import { useState, useEffect } from "react";
import {
  useClearNotificationsMutation,
  useGetNotificationsQuery,
  useMarkNotificationsMutation,
  useMarkAllNotificationsAsReadMutation,
  useGetNotificationStatsQuery,
  NotificationCategory,
  NotificationPriority,
  notifcationsApi,
  useClearAllNotificationsMutation
} from "../../api/endpoints/notificationsApi";

interface NotificationFilters {
  read?: boolean;
  category?: NotificationCategory;
  priority?: NotificationPriority;
  limit?: number;
  offset?: number;
  sort?: string;
  type?: number;
  action_required?: boolean;
  high_priority?: boolean;
}

const useNotifications = (initialFilters?: NotificationFilters) => {
  const [filters, setFilters] = useState<NotificationFilters>({
    limit: 50,
    offset: 0,
    ...initialFilters
  });

  const { data, isError, isLoading, refetch } = useGetNotificationsQuery(filters);
  const { data: stats } = useGetNotificationStatsQuery();
  const [markNotifications] = useMarkNotificationsMutation();
  const [markAllNotifications] = useMarkAllNotificationsAsReadMutation();
  const [markAllUnreadNotifications] = notifcationsApi.useMarkAllNotificationsAsUnreadMutation();
  const [deleteNotifications] = useClearNotificationsMutation();
  const [clearAllNotificationsMutation] = useClearAllNotificationsMutation();

  // Auto-refresh notifications every 30 seconds
  useEffect(() => {
    const id = setInterval(refetch, 30000);
    return () => clearInterval(id);
  }, [refetch]);

  const updateFilters = (newFilters: Partial<NotificationFilters>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: newFilters.offset !== undefined ? newFilters.offset : 0
    }));
  };

  const loadMore = () => {
    if (data?.hasMore) {
      setFilters(prev => ({
        ...prev,
        offset: (prev.offset || 0) + (prev.limit || 50)
      }));
    }
  };

  const markAsRead = (ids: number[]) => {
    markNotifications({ ids, read: true });
  };

  const markAsUnread = (ids: number[]) => {
    markNotifications({ ids, read: false });
  };

  const markActionClicked = (ids: number[]) => {
    markNotifications({ ids, action_clicked: true, read: true });
  };

  const updateStatus = (ids: number[], status: string) => {
    markNotifications({ ids, status });
  };

  const markAllAsRead = () => {
    markAllNotifications();
  };

  const markAllAsUnread = () => {
    markAllUnreadNotifications();
  };

  const deleteAll = () => {
    if (data?.notifications) {
      deleteNotifications(data.notifications.map(n => n.id));
    }
  };

  const clearAll = () => {
    clearAllNotificationsMutation();
  };

  const deleteSelected = (ids: number[]) => {
    deleteNotifications(ids);
  };

  const getNotificationsByCategory = (category: NotificationCategory) => {
    return data?.notifications.filter(n => n.category === category) || [];
  };

  const getNotificationsByPriority = (priority: NotificationPriority) => {
    return data?.notifications.filter(n => n.priority === priority) || [];
  };

  const getUnreadCount = () => {
    return data?.notifications.filter(n => !n.read).length || 0;
  };

  const getCriticalNotifications = () => {
    return data?.notifications.filter(n => n.priority === NotificationPriority.Critical) || [];
  };

  const getHighPriorityNotifications = () => {
    return data?.notifications.filter(n => 
      n.priority === NotificationPriority.High || n.priority === NotificationPriority.Critical
    ) || [];
  };

  return {
    // Data
    notifications: data?.notifications || [],
    stats,
    total: data?.total || 0,
    hasMore: data?.hasMore || false,
    
    // State
    isError,
    isLoading,
    filters,
    
    // Actions
    updateFilters,
    loadMore,
    markAsRead,
    markAsUnread,
    markActionClicked,
    updateStatus,
    markAllAsRead,
    markAllAsUnread,
    deleteAll,
    deleteSelected,
    clearAll,
    
    // Computed values
    unreadCount: stats?.unread ?? getUnreadCount(),
    newNotifications: (stats?.unread ?? getUnreadCount()) > 0,
    criticalNotifications: getCriticalNotifications(),
    highPriorityNotifications: getHighPriorityNotifications(),
    highPriorityCount: stats?.highPriority ?? 0,
    actionRequiredCount: stats?.actionRequired ?? 0,
    
    // Filtered views
    getNotificationsByCategory,
    getNotificationsByPriority,
    
    // Refresh
    refetch
  };
};

export default useNotifications;
